import { Link } from "wouter";
import { X, Search as SearchIcon, Sun, Moon, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sections, moreMenuItems, type Section } from "@/lib/navigationConfig";
import { cn } from "@/lib/utils";
import { useState, useEffect, useRef, useCallback } from "react";
import { needsCrossDomainNav, getCrossDomainHref, isCapitalDomain } from "@/lib/domainUtils";
import { useTheme } from "@/components/theme-provider";

interface FullNavPanelProps {
  open: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

const defaultSectionOrder = ["discover", "network", "capital", "programs", "membership"] as Section[];
const capitalSectionOrder = ["capital", "discover", "network", "programs", "membership"] as Section[];

export function FullNavPanel({ open, onClose, isAdmin }: FullNavPanelProps) {
  const { theme, setTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const capitalDomain = isCapitalDomain();
  const sectionOrder = capitalDomain ? capitalSectionOrder : defaultSectionOrder;
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      closeButtonRef.current?.focus();
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  const q = searchQuery.toLowerCase();

  // Helper: filter items for a group against the current search query
  const matchItems = (items: typeof sections[typeof sectionOrder[0]]["hamburgerGroups"][0]["items"]) =>
    !searchQuery
      ? items
      : items.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            (item.description && item.description.toLowerCase().includes(q))
        );

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={capitalDomain ? "Chief in Tech Capital navigation" : "Site navigation"}
      className="fixed inset-0 z-[60] bg-background overflow-y-auto"
      data-testid="full-nav-panel"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-lg font-semibold" data-testid="text-nav-panel-title">
            {capitalDomain ? "Chief in Tech Capital navigation" : "Navigation"}
          </h2>
          <Button
            ref={closeButtonRef}
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="button-close-nav-panel"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close navigation</span>
          </Button>
        </div>

        <div className="mb-8 max-w-full sm:max-w-md">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={capitalDomain ? "Search Capital resources..." : "Search the platform..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="input-nav-search"
            />
          </div>
        </div>

        <nav aria-label="Full site navigation">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {sectionOrder.map((key) => {
              const section = sections[key];
              const crossDomain = needsCrossDomainNav(key);

              // Pre-compute which groups have matching items
              const groupsWithMatches = section.hamburgerGroups.map((group) => ({
                group,
                filteredItems: matchItems(group.items),
              })).filter(({ filteredItems }) => filteredItems.length > 0);

              // Skip entire section when searching and nothing matches
              if (searchQuery && groupsWithMatches.length === 0) return null;

              const headingContent = (
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2 hover:text-primary transition-colors">
                  <section.icon className="h-4 w-4" />
                  {section.label}
                </h3>
              );
              return (
                <div key={key} data-testid={`nav-section-${key}`}>
                  {crossDomain ? (
                    <a href={getCrossDomainHref(key, section.href)} onClick={onClose}>
                      {headingContent}
                    </a>
                  ) : (
                    <Link href={section.href} onClick={onClose}>
                      {headingContent}
                    </Link>
                  )}
                  <ul className="space-y-3 sm:space-y-4">
                    {groupsWithMatches.map(({ group, filteredItems }) => (
                      <li key={group.title}>
                        {group.title && (
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                            {group.title}
                          </p>
                        )}
                        <ul className="space-y-0.5 sm:space-y-1">
                          {filteredItems.map((item) => {
                              const itemLinkClass = cn(
                                "flex items-start gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                                "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                              );
                              const itemContent = (
                                <>
                                  {item.icon && <item.icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />}
                                  <div>
                                    <span className="font-medium">{item.label}</span>
                                    {item.description && (
                                      <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                                    )}
                                  </div>
                                </>
                              );
                              return (
                                <li key={item.href}>
                                  {crossDomain ? (
                                    <a
                                      href={getCrossDomainHref(key, item.href)}
                                      onClick={onClose}
                                      className={itemLinkClass}
                                      data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {itemContent}
                                    </a>
                                  ) : (
                                    <Link
                                      href={item.href}
                                      onClick={onClose}
                                      className={itemLinkClass}
                                      data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {itemContent}
                                    </Link>
                                  )}
                                </li>
                              );
                            })}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* More / footer links — hidden during search (not searchable) */}
          {!searchQuery && (moreMenuItems.length > 0 || isAdmin) && (
            <div className="mt-8 pt-6 border-t">
              <div className="flex flex-wrap gap-4">
                {moreMenuItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`nav-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </Link>
                ))}
                {isAdmin && (
                  <Link
                    href="/admin"
                    onClick={onClose}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
                    data-testid="nav-more-admin"
                  >
                    Admin Dashboard
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Theme switcher — hidden during search */}
          {!searchQuery && (
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3" data-testid="text-theme-label">Theme</h3>
              <div className="flex items-center gap-2">
                {([
                  { value: "light" as const, icon: Sun, label: "Light" },
                  { value: "dark" as const, icon: Moon, label: "Dark" },
                  { value: "system" as const, icon: Monitor, label: "System" },
                ]).map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTheme(option.value)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      theme === option.value
                        ? "bg-secondary text-secondary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                    data-testid={`button-theme-${option.value}`}
                  >
                    <option.icon className="h-4 w-4" />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </nav>
      </div>
    </div>
  );
}
